import { backArrowIconSvg, closeIconSvg, brandingLogoSvg, arrowIconSvg } from '../icons/icons.js';
import { hexToRgba } from '../utils/color.js';
import { resolveImageUrl } from '../utils/helpers.js';

/**
 * Base Question class.
 * All question types extend this class and override renderContent() / renderNextButton().
 */
export class Question {
    constructor(question, ctx) {
        this.question = question;
        this.ctx = ctx;
        this.style = ctx.style;
        this.shadowRoot = ctx.shadowRoot;
        this.startIndex = ctx.startIndex;
        this.currentIndex = ctx.currentIndex;
        this.element = null;
        this.language = ctx.language || 'de';
    }

    renderBase() {
        this.ctx.nextAndSkipButtonBlockIds.push('btnElement' + this.question.id);

        const container = document.createElement('div');
        container.className = `ph-question--${this.question.type.toLowerCase().replace(/\s+/g, '-')}`;
        container.classList.add('ph-survey');

        const isAutoHeight = this.ctx.surveyType === 'onsite' || 
                             this.ctx.surveyType === 'previewLink' || 
                             this.ctx.surveyType === 'cssSelectorEmbed' || 
                             this.ctx.surveyType === 'previewCssSelectorEmbed' ||
                             this.style?.heightUnit === 'auto' || 
                             (!this.style?.heightUnit && !this.style?.heightValue) || 
                             this.style?.heightValue === 'auto';

        if (isAutoHeight) {
            container.classList.add('ph-survey--auto-height');
        }

        let headerElement = this.headerElement();
        if (headerElement) container.appendChild(headerElement);

        // Branding is positioned in the header, but the header itself is disabled:
        // show the logo/text centered at the very top (above the progress bar).
        if (this.hasHeaderBranding() && !this.hasHeader()) {
            let topBrandingElement = this.customBrandingElement();
            if (topBrandingElement) {
                topBrandingElement.classList.add('ph-branding--top');
                container.appendChild(topBrandingElement);
            }
        }

        let topbarElement = this.topbarElement(this.question, this.style);
        container.appendChild(topbarElement);

        if (this.style.showAvatar === true || this.style.showAvatar === '1' || this.style.showAvatar === 1) {
            let employeeInfoElement = this.employeeInfoElement(this.style);
            if (employeeInfoElement) container.appendChild(employeeInfoElement);
        }

        let questionBody = document.createElement('div');
        questionBody.className = 'ph-question__body';

        let imageElement = this.imageElement();
        if (imageElement) questionBody.appendChild(imageElement);

        let titleAndDescriptionElement = this.quenstionTitleAndDescriptionElement(this.question);
        questionBody.appendChild(titleAndDescriptionElement);

        if (this.question.type === 'Multi Select') {
            let warningElement = document.createElement('div');
            warningElement.id = 'warningElement' + this.question.id;
            warningElement.classList.add('ph-warning');
            warningElement.style.display = 'none';
            warningElement.style.marginBottom = '-8px';
            questionBody.appendChild(warningElement);
        }

        let content = this.renderContent();
        if (content) questionBody.appendChild(content);

        container.appendChild(questionBody);

        let errorElement = document.createElement('div');
        errorElement.id = 'errorElement' + this.question.id;
        errorElement.setAttribute('role', 'alert');
        errorElement.setAttribute('aria-live', 'assertive');
        container.appendChild(errorElement);

        let btnElement = document.createElement('div');
        btnElement.classList.add('ph-center');
        btnElement.style.width = '100%';
        btnElement.id = 'btnElement' + this.question.id;
        container.appendChild(btnElement);

        let skipBtnElement = this.skipButtonElement();
        if (skipBtnElement) btnElement.appendChild(skipBtnElement);

        let nextBtnElement = this.renderNextButton();
        if (nextBtnElement) btnElement.appendChild(nextBtnElement);

        let brandingElement = this.brandingElement();
        if (brandingElement) container.appendChild(brandingElement);

        return container;
    }

    topbarElement(question, style) {
        let topBar = document.createElement('div');
        topBar.classList.add('ph-topbar');

        if ((question.type === 'Thank You Message' || question.type === 'Engagement Trigger') && (style.goBack === true || style.goBack === '1' || style.goBack === 1 || style.canBeClosed === true || style.canBeClosed === '1' || style.canBeClosed === 1)) {
            topBar.style.marginTop = '0px';
        }

        if ((question.type !== 'Thank You Message' && question.type !== 'Engagement Trigger') && this.startIndex === this.currentIndex && (style.canBeClosed === true || style.canBeClosed === '1' || style.canBeClosed === 1)) {
            topBar.style.marginTop = '0px';
        }

        if ((question.type !== 'Thank You Message' && question.type !== 'Engagement Trigger') && this.startIndex !== this.currentIndex && (style.goBack === true || style.goBack === '1' || style.goBack === 1 || style.canBeClosed === true || style.canBeClosed === '1' || style.canBeClosed === 1)) {
            topBar.style.marginTop = '0px';
        }

        let hasGoBack = (style.goBack === true || style.goBack === '1' || style.goBack === 1) && this.startIndex !== this.currentIndex;
        let hasProgressBar = (style.showProgressBar !== false && style.showProgressBar !== '0' && style.showProgressBar !== 0) && question.type !== 'Engagement Trigger';
        let hasHeader = this.hasHeader();
        let hasCloseButton = !hasHeader && (style.allowCloseMinimize === true || style.allowCloseMinimize === '1' || style.allowCloseMinimize === 1) && this.ctx.surveyType !== 'embed' && this.ctx.surveyType !== 'cssSelectorEmbed' && this.ctx.surveyType !== 'previewCssSelectorEmbed' && this.ctx.surveyType !== 'embedded' && this.ctx.surveyType !== 'standalone' && this.ctx.surveyType !== 'previewStandalone' && this.ctx.previewType !== 'embed';
        
        if (hasGoBack) {
            let goBackButtonElement = this.goBackButton();
            topBar.appendChild(goBackButtonElement);
        }

        if (hasProgressBar) {
            let progressBarElement = this.progressBar(question, style);
            topBar.appendChild(progressBarElement);
        }

        if (hasCloseButton) {
            let closeButtonElement = this.closeButton();
            topBar.appendChild(closeButtonElement);
        }

        if (!hasGoBack && !hasProgressBar && !hasCloseButton) {
            topBar.style.display = 'none';
        }

        if (!hasGoBack && !hasProgressBar && hasCloseButton) {
            topBar.classList.add('ph-topbar--close-only');
        }

        return topBar;
    }

    /**
     * Custom branding (logo/text) placed in the header. Only true when the
     * project may use custom branding and the branding is enabled.
     */
    hasHeaderBranding() {
        const canCustomBranding = this.ctx.can?.customBranding === true;
        const showBranding = this.style.branding?.showBranding === true || this.style.branding?.showBranding === '1';
        return canCustomBranding && showBranding && this.style.branding?.position === 'header';
    }

    hasHeader() {
        const header = this.style.header;
        const canCustomHeader = this.ctx.can?.customHeader === true;
        const showHeader = header?.showHeader === true || header?.showHeader === '1';
        return showHeader && canCustomHeader;
    }

    headerElement() {
        const header = this.style.header ?? {};
        if (this.hasHeader()) {
            const headerDiv = document.createElement('div');
            headerDiv.classList.add('ph-header');
            if (header.headerColor) headerDiv.style.backgroundColor = header.headerColor;

            if (this.hasHeaderBranding()) {
                const brandingBox = document.createElement('div');
                brandingBox.classList.add('ph-header__branding');

                if (this.style.branding?.type === 'logo') {
                    if (this.ctx.brandingLogo) {
                        //width can be 'auto' or a number (in which case we add 'px')
                        const width = this.style.branding?.width === 'auto' ? this.style.branding?.width : (this.style.branding?.width ? this.style.branding?.width + 'px' : 'auto');
                        const height = this.style.branding?.height === 'auto' ? this.style.branding?.height : (this.style.branding?.height ? this.style.branding?.height + 'px' : 'auto');
                        brandingBox.innerHTML = `<img src="${this.ctx.brandingLogo}" style="height: ${height}; width: ${width}; display: block; object-fit: cover;" alt="branding-logo" />`;
                    }
                } else if (this.style.branding?.type === 'text') {
                    brandingBox.textContent = this.style.branding?.text ?? '';
                    brandingBox.style.color = header.logoColor || 'white';
                }
                headerDiv.appendChild(brandingBox);
            } else {
                headerDiv.appendChild(document.createElement('div')); // Spacer
            }

            // Close Icon
            const showCloseButton = (this.style.allowCloseMinimize === true || this.style.allowCloseMinimize === '1' || this.style.allowCloseMinimize === 1) &&
                                    this.ctx.surveyType !== 'standalone' &&
                                    this.ctx.surveyType !== 'previewStandalone' &&
                                    this.ctx.surveyType !== 'embed' &&
                                    this.ctx.surveyType !== 'embedded' &&
                                    this.ctx.surveyType !== 'cssSelectorEmbed' &&
                                    this.ctx.previewType !== 'embed';

            if (showCloseButton) {
                const closeBtn = document.createElement('div');
                closeBtn.classList.add('ph-header__close');
                closeBtn.style.color = header.logoColor;
                const isMinimize = (this.style.allowCloseMinimize === true || this.style.allowCloseMinimize === '1') &&
                    this.style.closeType === 'minimize' &&
                    this.question.type !== 'Thank You Message';
                closeBtn.innerHTML = isMinimize ? arrowIconSvg('currentColor') : closeIconSvg();
                closeBtn.onclick = () => {
                    if (this.ctx.isPreviewMode && this.ctx.surveyType !== 'previewLink' && !isMinimize) return;
                    this.ctx.trackBehavior(this.question, 'closed');
                    this.ctx.closeSurvey();
                };
                headerDiv.appendChild(closeBtn);
            } else {
                headerDiv.appendChild(document.createElement('div')); // Spacer
            }

            return headerDiv;
        }
        return null;
    }

    setAriaInvalid(inputElement) {
        if (inputElement) {
            inputElement.setAttribute('aria-invalid', 'true');
            inputElement.setAttribute('aria-errormessage', 'errorElement' + this.question.id);
        }
    }

    clearAriaInvalid(inputElement) {
        if (inputElement) {
            inputElement.removeAttribute('aria-invalid');
            inputElement.removeAttribute('aria-errormessage');
        }
    }

    makeKeyboardActivatable(element) {
        element.setAttribute('role', 'button');
        element.setAttribute('tabindex', '0');
        element.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                element.click();
            }
        });
    }

    goBackButton() {
        let backBtn = document.createElement('button');
        backBtn.classList.add('ph-topbar__back');
        backBtn.setAttribute('aria-label', 'Go back');
        backBtn.innerHTML = backArrowIconSvg();
        backBtn.onclick = () => this.ctx.goBack();

        return backBtn;
    }

    progressBar(question, style) {
        let progressBarContainer = document.createElement('div');
        progressBarContainer.classList.add('ph-progress');
        progressBarContainer.setAttribute('role', 'progressbar');
        progressBarContainer.setAttribute('aria-valuemin', '0');
        progressBarContainer.setAttribute('aria-label', 'Survey progress');

        style.progressBarSettings = style.progressBarSettings ?? {
            progressBarStyle: 'continuous',
            showProgressNumber: true,
            progressNumberType: 'percent',
            excludeOptionalQuestions: false,
        };

        // Calculate total questions (optionally excluding optional questions)
        let questionsForProgress = this.ctx.questions.filter(q => {
            let type = q.properties ? q.properties.type : q.type;
            return type !== 'Thank You Message' && type !== 'Engagement Trigger';
        });

        if (style.progressBarSettings.excludeOptionalQuestions === true || style.progressBarSettings.excludeOptionalQuestions === '1') {
            questionsForProgress = questionsForProgress.filter(q => {
                let isObligated = q.properties ? q.properties.isObligated : q.isObligated;
                return isObligated !== false;
            });
        }
        let totalSteps = questionsForProgress.length;
        let currentStep = this.currentIndex - 1;

        let numberOfExcludedQuestionsBeforeCurrentQuestion = 0;
        if (style.progressBarSettings.excludeOptionalQuestions === true || style.progressBarSettings.excludeOptionalQuestions === '1') {
            let questionsWithoutTYMandET = this.ctx.questions.filter(q => {
                let type = q.properties ? q.properties.type : q.type;
                return type !== 'Thank You Message' && type !== 'Engagement Trigger';
            });
            let questionsBeforeCurrent = questionsWithoutTYMandET.slice(0, this.currentIndex - 1);
            numberOfExcludedQuestionsBeforeCurrentQuestion = questionsBeforeCurrent.filter(q => {
                let isObligated = q.properties ? q.properties.isObligated : q.isObligated;
                return isObligated === false || isObligated === '0' || isObligated === 0;
            }).length;

            if (numberOfExcludedQuestionsBeforeCurrentQuestion > 0 && (question.properties ? question.properties.type !== 'Thank You Message' : question.type !== 'Thank You Message')) {
                currentStep = currentStep - numberOfExcludedQuestionsBeforeCurrentQuestion;
            }
        }

        // For continuous bar, we need percentage
        let progressBarWidth = totalSteps > 0 ? ((currentStep) / totalSteps) * 100 : 0;

        // Handle special question types
        if (this.question.type === 'Thank You Message') {
            progressBarWidth = 100;
            currentStep = totalSteps;
        } else if (this.question.type === 'Engagement Trigger') {
            progressBarWidth = 0;
            currentStep = 0;
        }

        progressBarContainer.setAttribute('aria-valuenow', String(currentStep));
        progressBarContainer.setAttribute('aria-valuemax', String(totalSteps));
        progressBarContainer.setAttribute('aria-valuetext', `Step ${currentStep} of ${totalSteps}`);

        const isSteppedStyle = style.progressBarSettings.progressBarStyle === 'stepped';
        const isTextOnlyStyle = style.progressBarSettings.progressBarStyle === 'textOnly';

        // --- Info Column ---
        let infoColumn = document.createElement('div');
        infoColumn.classList.add('ph-progress__info');
        progressBarContainer.appendChild(infoColumn);

        const showProgressNumber = style.progressBarSettings.showProgressNumber === true || style.progressBarSettings.showProgressNumber === '1';

        // --- Label Row ---
        if (isTextOnlyStyle || showProgressNumber) {
            let labelRow = document.createElement('div');
            labelRow.classList.add('ph-progress__label-row');
            labelRow.style.position = 'relative';

            if (isTextOnlyStyle) {
                let textLabel = document.createElement('div');
                textLabel.classList.add('ph-progress__label');
                textLabel.style.justifyContent = 'center';
                labelRow.style.justifyContent = 'center';
                progressBarContainer.style.padding = '0';
                textLabel.style.fontSize = '16px';
                textLabel.style.textAlign = 'center';

                let displayStep = currentStep;
                if (this.language === 'de') {
                    textLabel.textContent = `Frage ${displayStep} von ${totalSteps}`;
                } else if (this.language === 'en') {
                    textLabel.textContent = `Question ${displayStep} of ${totalSteps}`;
                }
                textLabel.setAttribute('lang', this.language);
                labelRow.appendChild(textLabel);
            } else if (showProgressNumber) {
                let textLabel = document.createElement('div');
                textLabel.classList.add('ph-progress__label');

                if (style.progressBarSettings.progressNumberType === 'steps') {
                    textLabel.textContent = `${currentStep}/${totalSteps}`;
                } else {
                    textLabel.textContent = `${Math.round(progressBarWidth)}%`;
                }

                if (isSteppedStyle && totalSteps > 0) {
                    textLabel.style.position = 'absolute';
                    if (currentStep === 0) {
                        textLabel.style.left = '0%';
                    } else {
                        let centerPercent = ((currentStep - 0.5) / totalSteps) * 100;
                        textLabel.style.left = `${centerPercent}%`;
                        textLabel.style.transform = 'translateX(-50%)';
                    }
                    
                    textLabel.style.order = 'unset';
                    textLabel.style.flex = 'unset';
                } else if (!isSteppedStyle) {
                    textLabel.style.position = 'absolute';
                    textLabel.style.left = `${progressBarWidth}%`;
                    textLabel.style.transform = 'translateX(-50%)';
                    textLabel.style.order = 'unset';
                    textLabel.style.flex = 'unset';
                }

                labelRow.appendChild(textLabel);
            }

            infoColumn.appendChild(labelRow);
        }

        // --- Track Row ---
        if (!isTextOnlyStyle) {
            let trackContainer = document.createElement('div');
            trackContainer.classList.add('ph-progress__track');

            if (isSteppedStyle && totalSteps > 0) {
                trackContainer.style.background = 'transparent';
                trackContainer.style.gap = '4px';

                for (let i = 0; i < totalSteps; i++) {
                    let segment = document.createElement('div');
                    segment.style.flex = '1';
                    segment.style.height = '4px';
                    segment.style.borderRadius = '8px';
                    segment.style.backgroundColor = i < currentStep
                        ? style.elementColor
                        : hexToRgba(style.textColor, 0.16);
                    trackContainer.appendChild(segment);
                }
            } else {
                let fill = document.createElement('div');
                fill.classList.add('ph-progress__fill');
                fill.style.width = `${progressBarWidth}%`;
                trackContainer.appendChild(fill);
            }

            infoColumn.appendChild(trackContainer);
        }

        return progressBarContainer;
    }

    closeButton() {
        let closeButtonButton = document.createElement('button');
        closeButtonButton.classList.add('ph-topbar__close');
        closeButtonButton.setAttribute('aria-label', 'Close survey');

        const isMinimize = (this.ctx.style.allowCloseMinimize === true || this.ctx.style.allowCloseMinimize === '1') &&
            this.ctx.style.closeType === 'minimize' &&
            this.question.type !== 'Thank You Message';
        closeButtonButton.innerHTML = isMinimize ? arrowIconSvg('currentColor') : closeIconSvg();
        closeButtonButton.onclick = () => {
            if (this.ctx.isPreviewMode && this.ctx.surveyType !== 'previewLink' && !isMinimize) {
                return;
            }
            // Same behaviour tracking as the custom-header close button
            this.ctx.trackBehavior(this.question, 'closed');
            this.ctx.closeSurvey();
        };

        return closeButtonButton;
    }

    employeeInfoElement(style) {
        const hasContent = Boolean(this.ctx.avatar || style.nameOfCoworkerAndCompany || style.jobTitle);
        if (!hasContent) {
            return null;
        }

        let employeeInfoElement = document.createElement('div');
        employeeInfoElement.classList.add('ph-row', 'ph-avatar-container');
        employeeInfoElement.style.display = 'flex';
        employeeInfoElement.style.textAlign = 'left';
        this.ctx.avatar ?
            employeeInfoElement.innerHTML = `<div class="ph-avatar">${this.ctx.avatar}</div><div class="ph-avatar__info"><div class="ph-avatar__name">${style.nameOfCoworkerAndCompany ?? ''}</div><div class="ph-avatar__title">${style.jobTitle ?? ''}</div></div>`
            : employeeInfoElement.innerHTML = `<div class="ph-avatar__info"><div class="ph-avatar__name">${style.nameOfCoworkerAndCompany ?? ''}</div><div class="ph-avatar__title">${style.jobTitle ?? ''}</div></div>`;
        return employeeInfoElement;
    }

    imageElement() {
        if (this.question.type !== 'Thank You Message') {
            return null;
        }

        const isShowImage = this.question.showImage === true || 
                            this.question.showImage === '1' || 
                            this.question.showImage === 1 ||
                            this.question.enableImage === true ||
                            this.question.enableImage === '1' ||
                            this.question.enableImage === 1;

        const imageUrl = this.question.imageUrl || this.question.image;

        if (!isShowImage || !imageUrl || typeof imageUrl !== 'string' || !imageUrl.trim()) {
            return null;
        }

        const container = document.createElement('div');
        container.className = 'ph-question__image-container';

        const img = document.createElement('img');
        img.className = 'ph-question__image';
        img.src = resolveImageUrl(imageUrl);
        img.alt = this.question.content || '';

        container.appendChild(img);
        return container;
    }

    quenstionTitleAndDescriptionElement(question) {
        let titleBox = document.createElement('div');
        titleBox.style.display = 'flex';
        titleBox.style.justifyContent = 'center';
        titleBox.style.alignItems = 'flex-start';
        titleBox.style.width = '100%';
        titleBox.style.gap = '8px';
        titleBox.style.flexDirection = 'column';

        let titleElement = document.createElement('div');
        titleElement.classList.add('ph-question__title');
        titleElement.setAttribute('role', 'heading');
        titleElement.setAttribute('aria-level', '2');
        titleElement.id = 'ph-title-' + question.id;
        titleElement.style.display = question.content ? 'block' : 'none';
        titleElement.textContent = question.content + '';
        titleBox.appendChild(titleElement);

        let descriptionElement = document.createElement('div');
        descriptionElement.classList.add('ph-question__description');
        descriptionElement.style.display = question.description ? 'block' : 'none';
        descriptionElement.textContent = question.description;
        titleBox.appendChild(descriptionElement);

        return titleBox;
    }

    skipButtonElement() {
        if (!this.question.isObligated) {
            let skipButton = document.createElement('button');
            skipButton.classList.add('ph-btn--secondary');

            skipButton.textContent = this.question.skipButtonName;
            skipButton.addEventListener('click', () => {
                let errorElement = this.ctx.getById(this.shadowRoot, 'errorElement' + this.question.id);
                errorElement.style.visibility = 'hidden';
                this.ctx.skip(true);
            });
            return skipButton;
        }
    }

    brandingElement() {
        const canCustomBranding = this.ctx.can?.customBranding === true;
        const showBranding = this.style.branding?.showBranding === true || this.style.branding?.showBranding === '1';

        if (!canCustomBranding) {
            // PollHero branding — always shown when customBranding is disabled
            let brandingElement = document.createElement('div');
            brandingElement.classList.add('ph-branding');
            brandingElement.innerHTML = `
                <div class="ph-branding__text">Powered by</div>
                <a target="_blank"
                rel="noopener noreferrer"
                aria-label="Poll Hero (opens in new tab)"
                href="https://www.poll-hero.com/?utm_source=${(new URL(window.location.href)).host}&utm_medium=${this.ctx.surveyId}&utm_content=onsite">
                    <div class="ph-branding__logo">
                        ${brandingLogoSvg}
                    </div>
                </a>
            `;
            return brandingElement;
        }

        if (showBranding && this.style.branding?.position !== 'header') {
            return this.customBrandingElement();
        }

        return null;
    }

    /**
     * Builds the custom branding block (.ph-branding) with either the
     * configured text or the custom logo. Returns null when nothing can be shown.
     */
    customBrandingElement() {
        if (this.style.branding?.type === 'text') {
            const text = this.style.branding?.text;
            if (!text) return null;
            let brandingElement = document.createElement('div');
            brandingElement.classList.add('ph-branding');
            brandingElement.innerHTML = `<div class="ph-branding__text">${text}</div>`;
            return brandingElement;
        }

        if (this.ctx.brandingLogo) {
            // Custom logo (default fallback for logo type)
            let brandingElement = document.createElement('div');
            brandingElement.classList.add('ph-branding');
            const width = this.style.branding?.width === 'auto' ? this.style.branding?.width : (this.style.branding?.width ? this.style.branding?.width + 'px' : 'auto');
            const height = this.style.branding?.height === 'auto' ? this.style.branding?.height : (this.style.branding?.height ? this.style.branding?.height + 'px' : 'auto');
            brandingElement.innerHTML = `
                <div class="ph-branding__logo">
                    <img src="${this.ctx.brandingLogo}" style="height: ${height}; width: ${width}; object-fit: cover;" alt="branding-logo" />
                </div>
            `;
            return brandingElement;
        }

        return null;
    }

    // Override in subclass
    renderContent() {
        return null;
    }

    // Override in subclass
    renderNextButton() {
    }

    mount(parentElement) {
        this.element = this.renderBase();
        parentElement.appendChild(this.element);
    }

    unmount() {
        if (this.element) {
            this.element.remove();
            this.element = null;
        }
    }
}
